import type { ComponentType, PropsWithChildren } from 'react';

import { FeatureProtectedView } from '@moniebook-web/components/molecules/feature-protected-view';
import { ProtectedView } from '@moniebook-web/components/molecules/protected-view';
import { Outlet, type RouteObject } from 'react-router';

import { StyleScope } from '@/features/shared/style-scope';

const ENABLE_CONNECT_ACCOUNTS_PAGE = 'moniebook.pos.enableConnectAccountsPage';
const ENABLE_TRANSACTIONS_PAGE = 'moniebook.pos.enableTransactionsPage';
const ENABLE_CHART_OF_ACCOUNT_PAGE = 'moniebook.pos.enableChartOfAccountPage';
const ENABLE_BILLS_PAGE = 'moniebook.pos.enableBillsPage';
const ENABLE_PROFIT_AND_LOSS_PAGE = 'moniebook.pos.enableProfitAndLossPage';
const ENABLE_CATEGORIES_PAGE = 'moniebook.pos.enableCategoriesPage';
const ENABLE_OTHER_ACCOUNTS_PAGE = 'moniebook.pos.enableOtherAccountsPage';

const CONNECTED_ACCOUNTS_PERMISSIONS = [
    'MANAGE_CONNECTED_ACCOUNT',
    'VIEW_CONNECTED_ACCOUNT',
    'CONNECT_ACCOUNT',
];
const TRANSACTIONS_PERMISSIONS = [
    'VIEW_TRANSACTION',
    'UPDATE_TRANSACTION',
    'REVIEW_TRANSACTION',
    'CATEGORIZE_TRANSACTION',
    'EXPORT_TRANSACTION',
    'CREATE_MANUAL_TRANSACTION',
    'DELETE_MANUAL_TRANSACTION',
];
const CHART_OF_ACCOUNTS_PERMISSIONS = ['VIEW_ACCOUNTS', 'MANAGE_ACCOUNTS'];
const OTHER_ACCOUNTS_PERMISSIONS = [
    'VIEW_OTHER_ACCOUNTS',
    'MANAGE_OTHER_ACCOUNTS',
];

const PROFIT_AND_LOSS_PERMISSIONS = ['VIEW_PL_REPORT'];

export const accountingRoutes: RouteObject[] = [
    {
        path: '',
        element: <StyleScope name='accounting-ui' />,
        children: [
            {
                path: '',
                async lazy() {
                    const { paywallProtectedView } = await unwrapFeatures();

                    const PaywallProtectedView =
                        (await paywallProtectedView().then(
                            (m) => m.default,
                        )) as ComponentType<PropsWithChildren>;

                    return {
                        Component: () => (
                            <PaywallProtectedView>
                                <Outlet />
                            </PaywallProtectedView>
                        ),
                    };
                },
                children: [
                    {
                        path: 'accounts',
                        element: (
                            <FeatureProtectedView
                                flag={ENABLE_CONNECT_ACCOUNTS_PAGE}
                                noAltView>
                                <ProtectedView
                                    accessCodeList={
                                        CONNECTED_ACCOUNTS_PERMISSIONS
                                    }
                                    grantAccessIfAny
                                />
                            </FeatureProtectedView>
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { connectedAccountList } =
                                        await unwrapFeatures();

                                    return connectedAccountList().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                            {
                                path: 'connect',
                                async lazy() {
                                    const { connectAccount } =
                                        await unwrapFeatures();
                                    return connectAccount().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: 'transactions',
                        element: (
                            <FeatureProtectedView
                                flag={ENABLE_TRANSACTIONS_PAGE}
                                noAltView>
                                <ProtectedView
                                    accessCodeList={TRANSACTIONS_PERMISSIONS}
                                    grantAccessIfAny>
                                    <Outlet />
                                </ProtectedView>
                            </FeatureProtectedView>
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { transactionSetupGuard } =
                                        await unwrapFeatures();
                                    return transactionSetupGuard().then(
                                        (m) => ({ Component: m.default }),
                                    );
                                },
                                children: [
                                    {
                                        path: '',
                                        async lazy() {
                                            const { transactionsList } =
                                                await unwrapFeatures();
                                            return transactionsList().then(
                                                (m) => ({
                                                    Component: m.default,
                                                }),
                                            );
                                        },
                                    },
                                ],
                            },
                            {
                                path: '',
                                element: (
                                    <ProtectedView
                                        accessCodeList={[
                                            'CREATE_MANUAL_TRANSACTION',
                                        ]}>
                                        <Outlet />
                                    </ProtectedView>
                                ),
                                children: [
                                    {
                                        path: 'create-manual-income',
                                        async lazy() {
                                            const { createIncomeTransaction } =
                                                await unwrapFeatures();
                                            return createIncomeTransaction().then(
                                                (m) => ({
                                                    Component: m.default,
                                                }),
                                            );
                                        },
                                    },
                                    {
                                        path: 'create-manual-expense',
                                        async lazy() {
                                            const { createExpenseTransaction } =
                                                await unwrapFeatures();
                                            return createExpenseTransaction().then(
                                                (m) => ({
                                                    Component: m.default,
                                                }),
                                            );
                                        },
                                    },
                                    {
                                        path: 'create-manual-internal-transfer',
                                        async lazy() {
                                            const { createInternalTransfer } =
                                                await unwrapFeatures();
                                            return createInternalTransfer().then(
                                                (m) => ({
                                                    Component: m.default,
                                                }),
                                            );
                                        },
                                    },
                                ],
                            },
                            {
                                path: '',
                                element: (
                                    <ProtectedView
                                        accessCodeList={['UPDATE_TRANSACTION']}>
                                        <Outlet />
                                    </ProtectedView>
                                ),
                                children: [
                                    {
                                        path: ':transactionId/edit-manual-income',
                                        async lazy() {
                                            const { editIncomeTransaction } =
                                                await unwrapFeatures();
                                            return editIncomeTransaction().then(
                                                (m) => ({
                                                    Component: m.default,
                                                }),
                                            );
                                        },
                                    },
                                    {
                                        path: ':transactionId/edit-manual-expense',
                                        async lazy() {
                                            const { editExpenseTransaction } =
                                                await unwrapFeatures();
                                            return editExpenseTransaction().then(
                                                (m) => ({
                                                    Component: m.default,
                                                }),
                                            );
                                        },
                                    },
                                    {
                                        path: ':transactionId/edit-manual-internal-transfer',
                                        async lazy() {
                                            const { editInternalTransfer } =
                                                await unwrapFeatures();
                                            return editInternalTransfer().then(
                                                (m) => ({
                                                    Component: m.default,
                                                }),
                                            );
                                        },
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: 'chart-of-account',
                        element: (
                            <FeatureProtectedView
                                flag={ENABLE_CHART_OF_ACCOUNT_PAGE}
                                noAltView>
                                <ProtectedView
                                    accessCodeList={
                                        CHART_OF_ACCOUNTS_PERMISSIONS
                                    }
                                    grantAccessIfAny
                                />
                            </FeatureProtectedView>
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { chartOfAccountList } =
                                        await unwrapFeatures();
                                    return chartOfAccountList().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                            {
                                path: '',
                                element: (
                                    <ProtectedView
                                        accessCodeList={['MANAGE_ACCOUNTS']}
                                    />
                                ),
                                children: [
                                    {
                                        path: 'create',
                                        async lazy() {
                                            const { createChartOfAccount } =
                                                await unwrapFeatures();
                                            return createChartOfAccount().then(
                                                (m) => ({
                                                    Component: m.default,
                                                }),
                                            );
                                        },
                                    },
                                    {
                                        path: 'edit/:accountId',
                                        async lazy() {
                                            const { editChartOfAccount } =
                                                await unwrapFeatures();
                                            return editChartOfAccount().then(
                                                (m) => ({
                                                    Component: m.default,
                                                }),
                                            );
                                        },
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: 'other-accounts',
                        element: (
                            <FeatureProtectedView
                                flag={ENABLE_OTHER_ACCOUNTS_PAGE}
                                noAltView>
                                <ProtectedView
                                    accessCodeList={OTHER_ACCOUNTS_PERMISSIONS}
                                    grantAccessIfAny>
                                    <Outlet />
                                </ProtectedView>
                            </FeatureProtectedView>
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { otherAccountsList } =
                                        await unwrapFeatures();
                                    return otherAccountsList().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                            {
                                path: ':accountId',
                                async lazy() {
                                    const { otherAccountDetails } =
                                        await unwrapFeatures();
                                    return otherAccountDetails().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: 'accounting-categories',
                        element: (
                            <FeatureProtectedView
                                flag={ENABLE_CATEGORIES_PAGE}
                                noAltView>
                                <ProtectedView
                                    accessCodeList={
                                        CHART_OF_ACCOUNTS_PERMISSIONS
                                    }
                                    grantAccessIfAny
                                />
                            </FeatureProtectedView>
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { categoriesList } =
                                        await unwrapFeatures();
                                    return categoriesList().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: 'bills',
                        element: (
                            <FeatureProtectedView flag={ENABLE_BILLS_PAGE}>
                                <Outlet />
                            </FeatureProtectedView>
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { billsListing } =
                                        await unwrapFeatures();
                                    return billsListing().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                            {
                                path: 'create-expense-bill',
                                async lazy() {
                                    const { createExpenseBill } =
                                        await unwrapFeatures();
                                    return createExpenseBill().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                            {
                                path: 'edit-expense-bill/:billId',
                                async lazy() {
                                    const { editExpenseBill } =
                                        await unwrapFeatures();
                                    return editExpenseBill().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                            {
                                path: 'create-purchase-order-bill',
                                async lazy() {
                                    const { createPurchaseOrderBill } =
                                        await unwrapFeatures();
                                    return createPurchaseOrderBill().then(
                                        (m) => ({ Component: m.default }),
                                    );
                                },
                            },
                            {
                                path: 'edit-purchase-order-bill/:billId',
                                async lazy() {
                                    const { editPurchaseOrderBill } =
                                        await unwrapFeatures();
                                    return editPurchaseOrderBill().then(
                                        (m) => ({ Component: m.default }),
                                    );
                                },
                            },
                        ],
                    },
                    {
                        path: 'profit-and-loss',
                        element: (
                            <FeatureProtectedView
                                flag={ENABLE_PROFIT_AND_LOSS_PAGE}
                                noAltView>
                                <ProtectedView
                                    accessCodeList={PROFIT_AND_LOSS_PERMISSIONS}
                                    grantAccessIfAny>
                                    <Outlet />
                                </ProtectedView>
                            </FeatureProtectedView>
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { profitAndLossList } =
                                        await unwrapFeatures();
                                    return profitAndLossList().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                            {
                                path: ':categoryId',
                                async lazy() {
                                    const {
                                        profitAndLossCategoryTransactions,
                                    } = await unwrapFeatures();
                                    return profitAndLossCategoryTransactions().then(
                                        (m) => ({
                                            Component: m.default,
                                        }),
                                    );
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    },
];

async function unwrapFeatures() {
    const features = await import('accounting/features');

    return features.default;
}
